import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";

import { ApiError, invitesApi } from "../../api";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { queryKeys } from "../../queryKeys";
import type {
  InviteFormConfig,
  InviteRequestSubmit,
  InviteStatusView,
  InviteSubmitResult,
  PublicInviteMeta,
} from "../../types/invite";
import { cleanHandle, cleanPhone } from "./inviteInput";
import { InviteLookupPanel, InviteStatusPanel, readInviteKey, writeInviteKey } from "./InviteStatusPanel";
import { InviteBrand, useInviteTheme } from "./inviteTheme";
import "./PublicInvitePage.css";

type Values = {
  name: string;
  phone: string;
  email: string;
  instagram: string;
  invited_by: string;
  occupation: string;
  attending: "" | "solo" | "couple";
  partner_name: string;
  partner_instagram: string;
  answers: Record<string, string>;
  confirmed: boolean[];
  whatsapp_opt_in: boolean;
  website: string;
};

type Errors = Record<string, string>;

const HANDLE_RE = /^[A-Za-z0-9._]{1,30}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export { cleanHandle, cleanPhone };

/** Instagram / WhatsApp / direct, from ?utm_source or the in-app browser's user agent. */
function detectSource(params: URLSearchParams): string {
  const utm = (params.get("utm_source") || "").toLowerCase();
  if (utm.includes("insta") || utm === "ig") return "Instagram";
  if (utm.includes("whatsapp") || utm === "wa") return "WhatsApp";
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  if (/Instagram/i.test(ua)) return "Instagram";
  if (/WhatsApp/i.test(ua)) return "WhatsApp";
  return utm ? utm.slice(0, 32) : "direct";
}

export function validateInvite(values: Values, config: InviteFormConfig, closed: boolean): Errors {
  const e: Errors = {};
  if (values.name.trim().length < 2) e.name = "Please enter your full name.";
  if (!/^[6-9]\d{9}$/.test(cleanPhone(values.phone))) e.phone = "Enter a 10-digit Indian mobile number.";
  if (!EMAIL_RE.test(values.email.trim())) e.email = "Enter a valid email address.";
  if (!HANDLE_RE.test(cleanHandle(values.instagram))) e.instagram = "Enter your Instagram handle.";
  if (!values.whatsapp_opt_in) e.whatsapp_opt_in = "We'll only reach you on WhatsApp, so please tick this.";
  if (closed) return e;

  if (config.ask_invited_by && !values.invited_by.trim()) e.invited_by = "Tell us who invited you.";
  if (config.ask_occupation && !values.occupation.trim()) e.occupation = "Tell us what you do.";
  if (config.allow_couple) {
    if (!values.attending) e.attending = `Choose ${config.solo_label} or ${config.couple_label}.`;
    if (values.attending === "couple") {
      if (values.partner_name.trim().length < 2) e.partner_name = `Enter your ${config.partner_label.toLowerCase()}'s full name.`;
      if (!HANDLE_RE.test(cleanHandle(values.partner_instagram)))
        e.partner_instagram = `Enter your ${config.partner_label.toLowerCase()}'s Instagram handle.`;
    }
  }
  for (const q of config.questions) {
    if (q.required && !(values.answers[q.id] || "").trim()) e[`q_${q.id}`] = q.type === "choice" ? "Pick one." : "A short answer is fine.";
  }
  if (config.confirmations.length && !values.confirmed.every(Boolean)) {
    e.confirmations = "Please tick all of these to continue.";
  }
  return e;
}

function toPayload(values: Values, closed: boolean, ref: string, utm: string): InviteRequestSubmit {
  const base: InviteRequestSubmit = {
    name: values.name.trim(),
    phone: `+91${cleanPhone(values.phone)}`,
    email: values.email.trim().toLowerCase(),
    instagram: cleanHandle(values.instagram),
    attending: "solo",
    answers: {},
    confirmed: false,
    whatsapp_opt_in: values.whatsapp_opt_in,
    ref: ref || null,
    utm_source: utm,
    website: values.website || null,
  };
  if (closed) return base;
  return {
    ...base,
    invited_by: values.invited_by.trim() || null,
    occupation: values.occupation.trim() || null,
    attending: values.attending === "couple" ? "couple" : "solo",
    partner_name: values.attending === "couple" ? values.partner_name.trim() : null,
    partner_instagram: values.attending === "couple" ? cleanHandle(values.partner_instagram) : null,
    answers: Object.fromEntries(Object.entries(values.answers).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v)),
    confirmed: values.confirmed.every(Boolean),
  };
}

const Field: React.FC<{
  name: string;
  label: React.ReactNode;
  error?: string;
  className?: string;
  question?: boolean;
  children: React.ReactNode;
  after?: React.ReactNode;
}> = ({ name, label, error, className, question, children, after }) => (
  <div className={`inv-field${error ? " is-invalid" : ""}${className ? ` ${className}` : ""}`} data-field={name}>
    <label className={`inv-label${question ? " inv-label--q" : ""}`} htmlFor={`inv-${name}`}>
      {label}
    </label>
    {children}
    {after ?? <span className="inv-error" id={`inv-${name}-err`}>{error}</span>}
  </div>
);

const PublicInvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const ref = (params.get("ref") || "").trim();
  const forceClosed = params.get("closed") === "1";
  const formRef = useRef<HTMLFormElement>(null);
  const doneRef = useRef<HTMLElement>(null);

  const metaQuery = useQuery<PublicInviteMeta>({
    queryKey: token ? queryKeys.publicInvite(token) : ["invites", "public", "idle"],
    queryFn: () => invitesApi.publicMeta(token!),
    enabled: Boolean(token),
    retry: 1,
    staleTime: 60_000,
  });
  const meta: PublicInviteMeta | undefined = metaQuery.data;
  const config = meta?.config;
  const closed = Boolean(meta?.closed) || forceClosed;
  const eventName = config?.display_name || meta?.event_name || "";
  useInviteTheme(config?.theme ?? "neon", config ? `${config.page_title} · ${eventName}` : undefined);

  const [values, setValues] = useState<Values>(() => ({
    name: "",
    phone: "",
    email: "",
    instagram: "",
    invited_by: ref,
    occupation: "",
    attending: "",
    partner_name: "",
    partner_instagram: "",
    answers: {},
    confirmed: [],
    whatsapp_opt_in: false,
    website: "",
  }));
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState("");
  const [result, setResult] = useState<InviteSubmitResult | null>(null);
  const utm = useMemo(() => detectSource(params), [params]);

  // Returning guest: their private key comes from the WhatsApp link (?t=) or this browser.
  const [accessKey, setAccessKey] = useState<string | null>(() => params.get("t") || (token ? readInviteKey(token) : null));
  const [lookingUp, setLookingUp] = useState(false);
  useEffect(() => {
    const fromLink = params.get("t");
    if (!token || !fromLink) return;
    writeInviteKey(token, fromLink);
    const url = new URL(window.location.href);
    url.searchParams.delete("t"); // keep the key out of screenshots / forwarded links
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  }, [params, token]);
  const myQuery = useQuery<InviteStatusView>({
    queryKey: ["invites", "me", token, accessKey],
    queryFn: () => invitesApi.myStatus(token!, accessKey!),
    enabled: Boolean(token && accessKey),
    retry: false,
  });
  const myView: InviteStatusView | undefined = myQuery.data;
  useEffect(() => {
    if (myQuery.error instanceof ApiError && myQuery.error.status === 404 && token) {
      writeInviteKey(token, null); // stale key (request removed / new link): show the form
      setAccessKey(null);
    }
  }, [myQuery.error, token]);
  const forgetMe = () => {
    if (token) writeInviteKey(token, null);
    setAccessKey(null);
    setLookingUp(false);
  };

  const set = <K extends keyof Values>(key: K, value: Values[K], errorKey: string = key) => {
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => {
      if (!e[errorKey]) return e;
      const next = { ...e };
      delete next[errorKey];
      return next;
    });
    setFormError("");
  };

  const focusFirst = (keys: string[]) => {
    const first = formRef.current?.querySelector<HTMLElement>(`[data-field="${keys[0]}"]`);
    if (!first) return;
    first.scrollIntoView({ behavior: "smooth", block: "center" });
    const focusable = first.querySelector<HTMLElement>("input, textarea");
    if (focusable) window.setTimeout(() => focusable.focus({ preventScroll: true }), 350);
  };

  const showDone = (res: InviteSubmitResult) => {
    setResult(res);
    if (token && res.access_token) writeInviteKey(token, res.access_token); // status on the next visit
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.setTimeout(() => doneRef.current?.focus({ preventScroll: true }), 50);
  };

  const submit = useMutation({
    mutationFn: (payload: InviteRequestSubmit) => invitesApi.publicSubmit(token!, payload),
    onSuccess: showDone,
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setErrors({ phone: err.message || "You've already requested an invitation." });
          focusFirst(["phone"]);
          return;
        }
        const detail = err.detail as { errors?: Errors } | undefined;
        if (err.status === 422 && detail?.errors) {
          setErrors(detail.errors);
          focusFirst(Object.keys(detail.errors));
          setFormError("Some details need a look above.");
          return;
        }
      }
      setFormError("Something went wrong. Please try again in a moment.");
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!config) return;
    if (values.website) {
      showDone({ status: "pending" }); // honeypot tripped: fail silently
      return;
    }
    const found = validateInvite(values, config, closed);
    const keys = Object.keys(found);
    setErrors(found);
    if (keys.length) {
      setFormError(keys.length === 1 ? "One thing needs a look above." : `${keys.length} things need a look above.`);
      focusFirst(keys);
      return;
    }
    submit.mutate(toPayload(values, closed, ref, utm));
  };

  const theme = config?.theme ?? "neon";

  if (metaQuery.isLoading) {
    return <div className={`inv inv--${theme}`} aria-busy="true" />;
  }
  if (!meta || !config) {
    return (
      <div className={`inv inv--${theme}`}>
        <div className="inv-status-page">
          <section className="inv-done">
            <h2>Link not found</h2>
            <p>This invite link is invalid or has expired.</p>
          </section>
        </div>
      </div>
    );
  }

  const sectionNumber = (() => {
    let n = 0;
    return () => String(++n).padStart(2, "0");
  })();
  const waitlisted = result?.status === "waitlisted" || closed;
  const privacy = config.privacy_line;

  return (
    <div className={`inv inv--${theme}`}>
      <div className="inv-page">
        <aside className="inv-aside">
          <section className="inv-card" aria-label="Invitation">
            <InviteBrand brand={config.brand} logoUrl={config.logo_url} />
            <h1>
              {config.headline}
              {config.tagline ? <span className="inv-tag">{config.tagline}</span> : null}
            </h1>
            {config.subhead ? <p className="inv-subhead">{config.subhead}</p> : null}
            <div className="inv-rule" aria-hidden="true">
              <span />
              <i />
              <span />
            </div>
            {config.details.length ? (
              <p className="inv-details">
                {config.details.map((d, i) => (
                  <React.Fragment key={d}>
                    {i > 0 ? " · " : null}
                    <span>{d}</span>
                  </React.Fragment>
                ))}
              </p>
            ) : null}
            {config.venue_note ? <p className="inv-venue-note">{config.venue_note}</p> : null}
          </section>
          <p className="inv-privacy inv-privacy--desktop">{privacy}</p>
        </aside>

        <main className="inv-main">
          {result ? (
            <section className="inv-done" ref={doneRef} tabIndex={-1} aria-live="polite">
              <div className="inv-rule" aria-hidden="true">
                <span />
                <i />
                <span />
              </div>
              <h2>{waitlisted ? config.waitlist_success_title : config.success_title}</h2>
              <p>{waitlisted ? config.waitlist_success_text : config.success_text}</p>
              {result.application_no ? <span className="inv-ref">Request {result.application_no}</span> : null}
            </section>
          ) : accessKey && (myView || myQuery.isLoading) ? (
            myView ? (
              <InviteStatusPanel
                formToken={token!}
                accessToken={accessKey}
                view={myView}
                onRefresh={() => void myQuery.refetch()}
                onNotMe={forgetMe}
              />
            ) : (
              <section className="inv-done" aria-busy="true" />
            )
          ) : lookingUp ? (
            <InviteLookupPanel
              formToken={token!}
              onVerified={(key) => {
                writeInviteKey(token!, key);
                setAccessKey(key);
                setLookingUp(false);
              }}
              onCancel={() => setLookingUp(false)}
            />
          ) : (
            <>
              {closed ? <p className="inv-banner">{config.closed_text}</p> : null}

              <form className="inv-form" ref={formRef} noValidate onSubmit={onSubmit}>
                <div className="inv-hp" aria-hidden="true">
                  <label htmlFor="inv-website">Website</label>
                  <input
                    id="inv-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={values.website}
                    onChange={(e) => set("website", e.target.value)}
                  />
                </div>

                <section className="inv-section" aria-labelledby="inv-s1">
                  <div className="inv-section-head">
                    <span className="inv-section-num">{sectionNumber()}</span>
                    <h2 className="inv-section-title" id="inv-s1">Your details</h2>
                  </div>

                  <div className="inv-grid inv-grid--2">
                    <Field name="name" label="Full name" error={errors.name}>
                      <input
                        className="inv-control"
                        id="inv-name"
                        type="text"
                        autoComplete="name"
                        maxLength={60}
                        value={values.name}
                        onChange={(e) => set("name", e.target.value)}
                        aria-invalid={Boolean(errors.name) || undefined}
                      />
                    </Field>

                    <Field name="phone" label="WhatsApp number" error={errors.phone}>
                      <div className="inv-phone">
                        <span className="inv-cc" aria-hidden="true">+91</span>
                        <input
                          className="inv-control"
                          id="inv-phone"
                          type="tel"
                          inputMode="numeric"
                          autoComplete="tel-national"
                          placeholder="10-digit mobile"
                          maxLength={14}
                          value={values.phone}
                          onChange={(e) => set("phone", e.target.value)}
                          aria-invalid={Boolean(errors.phone) || undefined}
                          aria-describedby="inv-phone-err"
                        />
                      </div>
                    </Field>

                    <Field name="email" label="Email" error={errors.email}>
                      <input
                        className="inv-control"
                        id="inv-email"
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        value={values.email}
                        onChange={(e) => set("email", e.target.value)}
                        aria-invalid={Boolean(errors.email) || undefined}
                      />
                    </Field>

                    <Field name="instagram" label="Instagram handle" error={errors.instagram}>
                      <input
                        className="inv-control"
                        id="inv-instagram"
                        type="text"
                        placeholder="@"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                        value={values.instagram}
                        onChange={(e) => set("instagram", e.target.value)}
                        aria-invalid={Boolean(errors.instagram) || undefined}
                      />
                    </Field>

                    {!closed && config.ask_invited_by ? (
                      <Field name="invited_by" label={config.invited_by_label} error={errors.invited_by}>
                        <input
                          className="inv-control"
                          id="inv-invited_by"
                          type="text"
                          placeholder={config.invited_by_placeholder}
                          maxLength={120}
                          value={values.invited_by}
                          onChange={(e) => set("invited_by", e.target.value)}
                          aria-invalid={Boolean(errors.invited_by) || undefined}
                        />
                      </Field>
                    ) : null}

                    {!closed && config.ask_occupation ? (
                      <Field name="occupation" label={config.occupation_label} error={errors.occupation}>
                        <input
                          className="inv-control"
                          id="inv-occupation"
                          type="text"
                          placeholder={config.occupation_placeholder}
                          maxLength={120}
                          value={values.occupation}
                          onChange={(e) => set("occupation", e.target.value)}
                          aria-invalid={Boolean(errors.occupation) || undefined}
                        />
                      </Field>
                    ) : null}

                    {!closed && config.allow_couple ? (
                      <fieldset className={`inv-field inv-span-2${errors.attending ? " is-invalid" : ""}`} data-field="attending">
                        <legend className="inv-label inv-legend">{config.attending_label}</legend>
                        <div className="inv-tiles">
                          {(["solo", "couple"] as const).map((opt) => (
                            <label key={opt} className={`inv-tile${values.attending === opt ? " is-checked" : ""}`}>
                              <input
                                type="radio"
                                name="attending"
                                value={opt}
                                checked={values.attending === opt}
                                onChange={() => set("attending", opt)}
                              />
                              {opt === "solo" ? config.solo_label : config.couple_label}
                            </label>
                          ))}
                        </div>
                        <span className="inv-error">{errors.attending}</span>
                      </fieldset>
                    ) : null}

                    {!closed && values.attending === "couple" ? (
                      <div className="inv-partner inv-span-2">
                        <div className="inv-grid inv-grid--2">
                          <Field name="partner_name" label={`${config.partner_label}'s full name`} error={errors.partner_name}>
                            <input
                              className="inv-control"
                              id="inv-partner_name"
                              type="text"
                              maxLength={60}
                              value={values.partner_name}
                              onChange={(e) => set("partner_name", e.target.value)}
                              aria-invalid={Boolean(errors.partner_name) || undefined}
                            />
                          </Field>
                          <Field
                            name="partner_instagram"
                            label={`${config.partner_label}'s Instagram handle`}
                            error={errors.partner_instagram}
                          >
                            <input
                              className="inv-control"
                              id="inv-partner_instagram"
                              type="text"
                              placeholder="@"
                              autoCapitalize="off"
                              autoCorrect="off"
                              spellCheck={false}
                              value={values.partner_instagram}
                              onChange={(e) => set("partner_instagram", e.target.value)}
                              aria-invalid={Boolean(errors.partner_instagram) || undefined}
                            />
                          </Field>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </section>

                {!closed && config.questions.length ? (
                  <section className="inv-section" aria-labelledby="inv-s2">
                    <div className="inv-section-head">
                      <span className="inv-section-num">{sectionNumber()}</span>
                      <h2 className="inv-section-title" id="inv-s2">{config.questions_title}</h2>
                      {config.questions_hint ? <p className="inv-section-hint">{config.questions_hint}</p> : null}
                    </div>
                    <div className="inv-grid">
                      {config.questions.map((q) => {
                        const key = `q_${q.id}`;
                        const answer = values.answers[q.id] || "";
                        const setAnswer = (v: string) => set("answers", { ...values.answers, [q.id]: v }, key);
                        const label = (
                          <>
                            {q.label}
                            {!q.required ? <> <span className="inv-opt">optional</span></> : null}
                          </>
                        );
                        if (q.type === "choice") {
                          return (
                            <fieldset key={q.id} className={`inv-field${errors[key] ? " is-invalid" : ""}`} data-field={key}>
                              <legend className="inv-label inv-label--q inv-legend">{label}</legend>
                              <div className="inv-chips">
                                {q.options.map((o) => (
                                  <label key={o.value} className={`inv-chip${answer === o.value ? " is-checked" : ""}`}>
                                    <input
                                      type="radio"
                                      name={key}
                                      value={o.value}
                                      checked={answer === o.value}
                                      onChange={() => setAnswer(o.value)}
                                    />
                                    {o.label}
                                  </label>
                                ))}
                              </div>
                              <span className="inv-error">{errors[key]}</span>
                            </fieldset>
                          );
                        }
                        return (
                          <Field
                            key={q.id}
                            name={key}
                            question
                            label={label}
                            error={errors[key]}
                            after={
                              q.type === "textarea" ? (
                                <div className="inv-meta">
                                  <span className="inv-error">{errors[key]}</span>
                                  <span className="inv-counter" aria-live="polite">
                                    {answer.length} / {q.max_length}
                                  </span>
                                </div>
                              ) : undefined
                            }
                          >
                            {q.type === "textarea" ? (
                              <textarea
                                className="inv-control"
                                id={`inv-${key}`}
                                rows={3}
                                maxLength={q.max_length}
                                placeholder={q.placeholder ?? undefined}
                                value={answer}
                                onChange={(e) => setAnswer(e.target.value)}
                              />
                            ) : (
                              <input
                                className="inv-control"
                                id={`inv-${key}`}
                                type="text"
                                maxLength={q.max_length}
                                placeholder={q.placeholder ?? undefined}
                                value={answer}
                                onChange={(e) => setAnswer(e.target.value)}
                              />
                            )}
                          </Field>
                        );
                      })}
                    </div>
                  </section>
                ) : null}

                <section className="inv-section" data-field="confirmations" aria-labelledby="inv-s3">
                  <div className="inv-section-head">
                    <span className="inv-section-num">{sectionNumber()}</span>
                    <h2 className="inv-section-title" id="inv-s3">Before you send</h2>
                  </div>
                  <div className="inv-checks">
                    {!closed
                      ? config.confirmations.map((text, i) => (
                          <label key={text} className="inv-check">
                            <input
                              type="checkbox"
                              checked={Boolean(values.confirmed[i])}
                              onChange={(e) => {
                                const next = config.confirmations.map((_, j) => (j === i ? e.target.checked : Boolean(values.confirmed[j])));
                                set("confirmed", next, "confirmations");
                              }}
                            />
                            {text}
                          </label>
                        ))
                      : null}
                    <label className="inv-check" data-field="whatsapp_opt_in">
                      <input
                        type="checkbox"
                        checked={values.whatsapp_opt_in}
                        onChange={(e) => set("whatsapp_opt_in", e.target.checked)}
                      />
                      {config.whatsapp_consent}
                    </label>
                  </div>
                  <span className="inv-error">{errors.confirmations || errors.whatsapp_opt_in}</span>
                </section>

                <div className="inv-actions">
                  <button className="inv-submit" type="submit" disabled={submit.isPending}>
                    {submit.isPending ? "Sending…" : closed ? "Join the waitlist" : config.submit_label}
                  </button>
                  <p className="inv-form-error" role="alert">{formError}</p>
                  <button type="button" className="inv-link-btn" onClick={() => setLookingUp(true)}>
                    Already requested? Check your status
                  </button>
                </div>
              </form>
            </>
          )}

          <p className="inv-privacy inv-privacy--mobile">{privacy}</p>
        </main>
      </div>
    </div>
  );
};

export default PublicInvitePage;
